import type { Tone } from "../../ds";
import type { Report } from "../../types";

export type ReportStatusKind = "archived" | "attention" | "draft" | "published" | "screens";

/** Screens that show the report (server-computed): ACTIVE office TVs showing
 * the published version, plus Laptops-group laptops, which show the live
 * draft -- so a draft can already be on laptops. */
export function screenCount(r: Report): number {
  return r.screen_count ?? 0;
}

/** One human status per report, used everywhere a report is listed. */
export function reportStatus(r: Report): { label: string; tone: Tone; hint: string; kind: ReportStatusKind } {
  if (r.status === "archived") return { kind: "archived", label: "Archived", tone: "neutral", hint: "Hidden from screens and viewers." };
  if (r.has_failed_publication) return { kind: "attention", label: "Needs attention", tone: "warning", hint: "The last publish attempt failed validation." };
  const n = screenCount(r);
  const names = r.screen_names ?? [];
  const showingOn = `Showing now on: ${names.join(", ")}${n > names.length ? ` and ${n - names.length} more` : ""}`;
  if (!r.current_published_version_id) {
    if (n > 0) return { kind: "draft", label: `Draft · on ${n} laptop${n === 1 ? "" : "s"}`, tone: "accent", hint: `Not published yet — office TVs don't show it, but test laptops show the live draft. ${showingOn}` };
    return { kind: "draft", label: "Draft", tone: "accent", hint: "Not published yet — only builders can see it." };
  }
  if (n > 0) {
    const laptops = r.laptop_count ?? 0;
    const tvs = n - laptops;
    const detail = laptops && tvs ? ` (${tvs} TV${tvs === 1 ? "" : "s"}, ${laptops} laptop${laptops === 1 ? "" : "s"})` : "";
    return { kind: "screens", label: `On ${n} screen${n === 1 ? "" : "s"}`, tone: "success", hint: `${showingOn}${detail}` };
  }
  return { kind: "published", label: "Published", tone: "info", hint: "Published, but no screen shows it yet. Choose screens under Screens & access." };
}
